import {
    type FileExtension,
    isPhotoExtension,
    isVideoExtension,
} from '../../../shared/domain/files/fileExtensions.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import type { RenderCachedImage } from '../image/WithCachedImage'
import { FileGuideNotFoundModal } from './FileGuideNotFoundModal'
import { FileGuideOfflineModal } from './FileGuideOfflineModal'
import { GuidePresentationWithParentInfo } from './GuidePresentationWithParentInfo'
import type { FileGuideExecution, ParentInfo } from './Model'
import { OtherFileGuide } from './OtherFileGuide'
import { PhotoFileGuide } from './PhotoFileGuide'
import { VideoFileGuide } from './VideoFileGuide'
import * as React from 'react'

interface Props {
    allowImageZoom: boolean
    deps: CommonDeps
    guideExecution: FileGuideExecution
    onBack: () => void
    onClickGuideParentInfo: (parentInfo: ParentInfo) => void
    onOpenFile: (downloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
    onReload: () => void
    renderCachedImage: RenderCachedImage
}

interface FileGuideProps {
    allowImageZoom: boolean
    deps: CommonDeps
    fileDownloadUrl: string
    fileExtension: FileExtension
    name: string
    onOpenFile: (fileDownloadUrl: string, fileExtension: FileExtension, name: string) => Promise<void>
}

export function FileGuidePresentation(props: Props) {
    const { allowImageZoom, deps, guideExecution, onBack, onOpenFile, onReload } = props

    const handleOpenFile = (downloadUrl: string, fileExtension: FileExtension, name: string) =>
        onOpenFile(downloadUrl, fileExtension, name).then(onBack)

    return (
        <GuidePresentationWithParentInfo
            deps={props.deps}
            guideParentInfo={props.guideExecution.parentInfo}
            onClickGuideParentInfo={props.onClickGuideParentInfo}
            renderCachedImage={props.renderCachedImage}
        >
            {props.guideExecution.customData.downloadUrl
                .map((maybeDownloadUrl) =>
                    maybeDownloadUrl
                        .map((fileDownloadUrl) => (
                            <FileGuide
                                allowImageZoom={allowImageZoom}
                                deps={deps}
                                fileDownloadUrl={fileDownloadUrl}
                                fileExtension={guideExecution.customData.fileExtension}
                                name={guideExecution.guide.name}
                                onOpenFile={handleOpenFile}
                            />
                        ))
                        .withDefault(<FileGuideNotFoundModal onBack={onBack} onTryAgain={onReload} />)
                )
                .withDefault(<FileGuideOfflineModal onBack={onBack} onTryAgain={onReload} />)}
        </GuidePresentationWithParentInfo>
    )
}

function FileGuide(props: FileGuideProps) {
    const { allowImageZoom, deps, fileDownloadUrl, fileExtension, name, onOpenFile } = props

    if (isPhotoExtension(fileExtension)) {
        return <PhotoFileGuide allowImageZoom={allowImageZoom} deps={deps} downloadUrl={fileDownloadUrl} />
    }

    if (isVideoExtension(fileExtension)) {
        return <VideoFileGuide downloadUrl={fileDownloadUrl} />
    }

    React.useEffect(() => {
        onOpenFile(fileDownloadUrl, fileExtension, name)
    })

    return <OtherFileGuide />
}
